import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Radio, RadioGroup } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { BearingType, ComponentSide, GeneratableBearing, HangerList } from '@paragon/contract-library';
import { PropertiesSubpanel } from '@paragon/layout-models';
import { DistanceField } from '@paragon/material';
import { humanCase } from '@paragon/utilities';

import {
  addBearingConnector,
  removeBearingConnector,
  updateBearingConnector,
} from '../../../../../../store/actions/editBearingActions';
import {
  deleteInsertionProfileBearing,
  updateInsertionProfileBearing,
} from '../../../../../../store/actions/profileActions';
import { useAppDispatch } from '../../../../../hooks';
import { ConnectorsList } from '../../../elements/ConnectorsList';
import { PropertiesSection } from '../../../properties/PropertiesSection';

interface Props {
  readonly bearing: GeneratableBearing;
  readonly index: number;
  readonly hangerMaterialList?: HangerList;
}

export function BearingRow({ bearing, index, hangerMaterialList }: Props) {
  const dispatch = useAppDispatch();

  const [expanded, setExpanded] = useState(false);

  const updateBearing = (props: Partial<GeneratableBearing>) => {
    const updatedBearing = { ...bearing, ...props } as GeneratableBearing; // TS can't verify this method is used safely
    dispatch(updateInsertionProfileBearing(updatedBearing, index));
  };

  const industryDistanceTolerance = 1 / 16;
  const bearingTypeOptions: readonly BearingType[] = [
    'DoublePlate',
    'ConcreteWall',
    'Hanger',
    'Ledger',
    'Header',
    'ToeNail',
  ];

  const isVertical = bearing.generatableBearingType === 'VerticalGeneratableBearing';

  return (
    <PropertiesSection
      title={`Bearing ${index + 1}`}
      expanded={expanded}
      onExpand={() => setExpanded(true)}
      onCollapse={() => setExpanded(false)}
    >
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, p: 1, justifyContent: 'space-between' }}>
          <PropertySelectField
            sx={{ m: 1, width: '25%' }}
            property="Bearing Type"
            value={bearing.bearingType}
            options={bearingTypeOptions}
            getName={humanCase}
            submit={(bearingType) => updateBearing({ bearingType })}
          />
          <DistanceField
            sx={{ m: 1, width: '25%' }}
            label="Offset"
            value={bearing.offset}
            onSubmit={(offset) => updateBearing({ offset })}
          />
          <RadioGroup
            row
            sx={{ m: 1 }}
            value={bearing.offsetSide}
            onChange={(event) => updateBearing({ offsetSide: event.target.value as ComponentSide })}
          >
            <FormControlLabel id="flipOffset" value="Left" control={<Radio />} label="From Left" />
            <FormControlLabel id="flipOffset" value="Right" control={<Radio />} label="From Right" />
          </RadioGroup>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, p: 1, justifyContent: 'space-evenly' }}>
          <DistanceField
            sx={{ m: 1 }}
            label="Width"
            value={bearing.width}
            min={industryDistanceTolerance}
            onSubmit={(width) => updateBearing({ width })}
          />
          {!isVertical && (
            <PropertySelectField
              sx={{ m: 1 }}
              property="Anchor"
              value={bearing.anchor}
              options={['Left', 'Center', 'Right'] as const}
              submit={(anchor) => updateBearing({ anchor })}
            />
          )}
          <PropertySelectField
            sx={{ m: 1 }}
            property="Chord Type"
            value={bearing.chordLocation}
            options={['Top', 'Bottom'] as const}
            submit={(chordLocation) => updateBearing({ chordLocation })}
          />
          {!isVertical && (
            <FormControlLabel
              sx={{ m: 1 }}
              control={
                <Checkbox
                  checked={bearing.isContinuous}
                  onChange={(event) => updateBearing({ isContinuous: event.target.checked })}
                />
              }
              label="Continuous"
            />
          )}
        </Box>

        <PropertiesSubpanel title="Connectors">
          <ConnectorsList
            connectors={bearing.connectors}
            hangerMaterialList={hangerMaterialList}
            onAddConnector={() => updateBearing(addBearingConnector(bearing))}
            onUpdateConnector={(connector) => updateBearing(updateBearingConnector(bearing, connector))}
            onRemoveConnector={(connectorGuid) => updateBearing(removeBearingConnector(bearing, connectorGuid))}
            isViewOnly={false}
          />
        </PropertiesSubpanel>

        <Box sx={{ display: 'flex', flexDirection: 'row', flex: 1, p: 1 }}>
          <Button
            sx={{ m: 'auto' }}
            size="small"
            variant="outlined"
            onClick={() => dispatch(deleteInsertionProfileBearing(index))}
          >
            Delete Bearing
          </Button>
        </Box>
      </Box>
    </PropertiesSection>
  );
}
